import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { AuthenticatorSetup } from '../components/AuthenticatorSetup.tsx';
import { Button } from '../components/Button.tsx';
import { FormField } from '../components/FormField.tsx';

interface InfoResponse {
  contact: { email: string; name: string | null };
  totp: { secret: string; qrDataUri: string };
  expiresAt: string;
}

interface CompleteResponse {
  status: 'ok';
  contact: { id: string; organizationId: string; email: string; name: string | null };
  backupCodes: string[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; info: InfoResponse }
  | { kind: 'done'; contactName: string; backupCodes: string[] };

export function PortalEnrollPage(): React.ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<InfoResponse>(`/api/portal/enroll/${encodeURIComponent(token)}`)
      .then((info) => {
        if (!cancelled) setState({ kind: 'ready', info });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof ApiError ? err.message : 'Enrollment link is invalid.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<CompleteResponse>(
        `/api/portal/enroll/${encodeURIComponent(token)}`,
        { method: 'POST', json: { password, totpCode: code.trim() } },
      );
      setState({
        kind: 'done',
        contactName: res.contact.name ?? res.contact.email,
        backupCodes: res.backupCodes,
      });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Enrollment failed.');
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'loading') {
    return <div className="text-[var(--color-muted)]">Loading enrollment…</div>;
  }
  if (state.kind === 'error') {
    return (
      <div className="mx-auto max-w-md rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-8">
        <h1 className="text-xl font-bold text-[var(--color-header)]">Enrollment unavailable</h1>
        <p className="mt-2 text-sm text-[var(--color-danger)]">{state.message}</p>
      </div>
    );
  }
  if (state.kind === 'done') {
    return (
      <div className="mx-auto max-w-md space-y-4 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-[var(--color-header)]">You're set, {state.contactName}.</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Save these backup codes somewhere safe — each can be used once if you lose access to your
          authenticator. They will not be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-4 font-mono text-sm">
          {state.backupCodes.map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
        <Button onClick={() => navigate('/portal', { replace: true })} className="w-full">
          Go to portal
        </Button>
      </div>
    );
  }

  const { info } = state;
  return (
    <div className="mx-auto max-w-xl space-y-6 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-sm">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">Set up your portal account</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Welcome{info.contact.name ? `, ${info.contact.name}` : ''} ({info.contact.email}). Choose a
          password, then link an authenticator app on your phone.
        </p>
      </div>
      <AuthenticatorSetup qrDataUri={info.totp.qrDataUri} secret={info.totp.secret} />
      <form onSubmit={submit} className="space-y-4">
        <FormField
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 12 characters with upper, lower, and a number."
        />
        <FormField
          label="Confirm password"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <FormField
          label="Authenticator code"
          name="code"
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          hint="The 6-digit code the app shows for T3 Tick3ting right now (step 3 above)."
        />
        {submitError && <p className="text-sm text-[var(--color-danger)]">{submitError}</p>}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Finishing setup…' : 'Finish setup'}
        </Button>
      </form>
    </div>
  );
}
